import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TornaAlMenu from "../components/TornaAlMenu";
import { authFetch } from "../utils/authFetch";

const API_URL = process.env.REACT_APP_API_URL || "";

const STEPS = [
  { key: "INVIATO", label: "Ordine inviato", icon: "📩" },
  { key: "IN_PREPARAZIONE", label: "In preparazione", icon: "⏳" },
  { key: "PRONTO_RITIRO", label: "Pronto per il ritiro", icon: "📦" },
  { key: "RITIRATO", label: "Ritirato", icon: "✅" },
];

const currentStep = (o) => {
  const app = String(o.app_status || "").toUpperCase();
  const st = String(o.status || "").toUpperCase();

  if (app === "RITIRATO") return 3;
  if (app === "PRONTO_RITIRO") return 2;
  if (app === "IN_PREPARAZIONE") return 1;
  if (["SUBMITTED", "INVIATO", "APPROVED", "DRAFT"].includes(st)) return 0;
  return 0;
};

const statusLabel = (o) => STEPS[currentStep(o)]?.label || "Ordine inviato";

const money = (v) => `€${Number(v || 0).toFixed(2)}`;

export default function AppOrdersPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");

  const load = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);

    setErr("");

    try {
      const res = await authFetch(`${API_URL}/api/app/ordini/storico/`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Errore caricamento ordini");
      setRows(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setErr(e.message || "Errore");
      setRows([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(() => load(true), 20000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="container mt-4">
      <TornaAlMenu to="/" />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="m-0">📦 I miei ordini</h2>
          <div className="text-muted small">
            Stato aggiornato automaticamente ogni 20 secondi
            {refreshing ? "…" : ""}
          </div>
        </div>
        <button className="btn btn-outline-primary" onClick={() => load()}>
          Aggiorna
        </button>
      </div>

      {loading ? (
        <p className="text-muted">Caricamento…</p>
      ) : err ? (
        <div className="alert alert-danger">{err}</div>
      ) : rows.length === 0 ? (
        <div className="alert alert-info">Non hai ancora ordini.</div>
      ) : (
        <div className="row g-3">
          {rows.map((o) => {
            const step = currentStep(o);

            return (
              <div className="col-12" key={o.id}>
                <div
                  className="card shadow-sm border-0 rounded-4"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/app/ordini/${o.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate(`/app/ordini/${o.id}`);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <div className="card-body p-4">
                    <div className="d-flex flex-wrap justify-content-between gap-2">
                      <div>
                        <div className="text-muted small">Ordine #{o.id}</div>
                        <h4 className="mb-1">{statusLabel(o)}</h4>
                        <span className={`badge ${
                          step === 3 ? "bg-secondary" :
                          step === 2 ? "bg-success" :
                          step === 1 ? "bg-primary" :
                          "bg-info text-dark"
                        }`}>
                          {statusLabel(o)}
                        </span>
                        <div className="text-muted mt-1">
                          {o.created_at ? new Date(o.created_at).toLocaleDateString("it-IT") : ""}
                        </div>
                      </div>

                      <div className="text-end">
                        <div className="text-muted small">Totale</div>
                        <div className="fs-4 fw-bold">{money(o.total || o.totale || o.grand_total || 0)}</div>
                        <div className="small text-primary mt-1">Apri dettaglio →</div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        {STEPS.map((s, idx) => (
                          <div className="text-center flex-fill" key={s.key}>
                            <div
                              className={`mx-auto rounded-circle d-flex align-items-center justify-content-center ${
                                idx <= step ? "bg-primary text-white" : "bg-light text-muted border"
                              }`}
                              style={{ width: 34, height: 34, fontWeight: 700 }}
                            >
                              {s.icon}
                            </div>
                            <div className={`small mt-2 ${idx <= step ? "fw-bold text-dark" : "text-muted"}`}>
                              {s.label}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="progress mt-3" style={{ height: 8 }}>
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
                          aria-valuenow={step + 1}
                          aria-valuemin="0"
                          aria-valuemax={STEPS.length}
                        />
                      </div>
                    </div>

                    {String(o.app_status || "").toUpperCase() === "PRONTO_RITIRO" && (
                      <div className="alert alert-success py-2 px-3 mt-3 mb-2 rounded-4 border-0">
                        <div className="fw-bold">📦 Pronto per il ritiro</div>
                      </div>
                    )}

                    {o.pickup_date && (
                      <div className="alert alert-light border mt-3 mb-0">
                        Ritiro: {o.pickup_date}
                        {o.pickup_time_from || o.pickup_time_to
                          ? ` — ${o.pickup_time_from || ""}${o.pickup_time_to ? " / " + o.pickup_time_to : ""}`
                          : ""}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
